import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

/**
 * Extracts a user-friendly error message from RTK Query error objects
 * @param error - The error object from RTK Query mutation or query
 * @returns A string message to display to the user
 */
export function getErrorMessage(error: FetchBaseQueryError | SerializedError | undefined): string {
  if (!error) {
    return 'An error occurred. Please try again later.';
  }

  if ('data' in error && error.data) {
    if (typeof error.data === 'object' && error.data && 'message' in error.data) {
      return String(error.data.message);
    }
    return String(error.data);
  }

  return 'An error occurred. Please try again later.';
}

/**
 * Install/update errors from the API's image App ID label check (SCRUM-1705),
 * rewritten so a user knows the fix is on the app developer's side. Returns
 * null for any other error so callers keep the default notification.
 */
export function getAppImageErrorMessage(
  error: FetchBaseQueryError | SerializedError | undefined,
): string | null {
  const message = getErrorMessage(error);
  if (message.includes('missing the com.osirisai.app_id label')) {
    return 'This version cannot be installed: its image has no com.osirisai.app_id label. Ask the app developer to rebuild and push it with the label.';
  }
  const mismatch = message.match(/manifest name (\S+) does not match App ID (\S+)/);
  if (mismatch) {
    return `This version cannot be installed: its image is labelled ${mismatch[1]}, but this app expects ${mismatch[2]}. Ask the app developer to fix the com.osirisai.app_id label.`;
  }
  return null;
}
